import {classNames, React, observer, useMemo} from '@api'
import {BuilderCard, BuilderCardProps} from '@modules/catalog'
import {ErrorBoundary, LoadingIndicator} from '@modules/common'
import {computed, useAsync, useAsObservableSource, useLocalStore} from '@api'
import {useCatalogItemShapeStore} from '@modules/whiteboard'
import {ICatalogFieldConfig, onSiteError, StoresInstance} from '@stores'
import {toJS} from 'mobx'
import * as css from './BuilderCardLoader.css'
import {useVipLocalStore, VipLocalStore} from '@design-board'

interface MyProps {
  id: string
  field?: Partial<ICatalogFieldConfig>
  isPopover?: boolean
  esIndex?: string
  useDefaultFieldsIfNoneSet?: boolean
  wrapperClassNames?: string
}

class BuilderCardLoaderStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: MyProps & Partial<BuilderCardProps>) {
    super(stores)
  }

  @computed get esIndex() {
    const {field, props: {workspace, esIndex, library}} = this
    return esIndex ?? field?.esIndex ?? library?.index ?? workspace?.catalog?.index
  }

  @computed get workspace() {
    return this.workspaces.wrapperByIndex[this.esIndex] ?? this.catalog.workspace
  }

  @computed get field() {
    const {props: {esIndex, field, nested, library}} = this
    return field ?? {esIndex: esIndex ?? nested?.field?.esIndex ?? library?.index}
  }

  /**
   * We can be rendering a nested card (say a style attached to an MPV, or a colorway attached to that style) and need to determine fields to render relative to the nested item's path
   */
  @computed get visibleCardFields() {
    const {field, props: {nested}, workspace, catalog} = this
    let r = catalog.visibleCardFieldsFor(field, nested?.parentField ?? null)

    return _.isEmpty(r) ? catalog.defaultFieldsFor(workspace) : r
  }

  @computed get id() {
    const {props: {data, id}, catalog} = this

    return catalog.parseIdFromPath(id ?? catalog.idFor(data))
  }
}

/**
 * Loads data for some ES entity by ID (usually in a popover from another entity with a link like MPV -> Colorway)
 */
export const BuilderCardLoader = React.memo(observer<MyProps & Partial<BuilderCardProps>, HTMLDivElement>(function BuilderCardLoader({className, wrapperClassNames, ...props}, ref) {
  const field = props.field ?? props.nested?.field,
    obsProps = useAsObservableSource({...props, field}),
    store = useVipLocalStore((ws) => new BuilderCardLoaderStore(ws, obsProps)),
    {es, visibleCardFields, catalog, id, workspace, stores: {catalog: {hasFinishedFirstLoad}}} = store,
    shapeStore = useCatalogItemShapeStore()

  const {error, loading, value} = useAsync(
      async () => {
        !id && !props?.nested?.data && console.warn(`No id found to load for ${field?.field}`, id)

        try {
          return !id ? {} : catalog.isOfflineItem(id) ? catalog.offlineItemsMap[id]?.item?.data
                                                      : es.get({index: props.esIndex ?? field?.esIndex ?? workspace?.catalog?.index ?? catalog.index, id})
        } catch (err) {
          debugger
          throw err
        }
      }, [field, id, catalog.es.refetchId, workspace]),
    data = useMemo(() => (value ?? props?.nested?.data ?? {id, hex: '#ffffff'}), [value, id, props?.nested])

  error && onSiteError(error)

  // console.log({workspace, index, id, value, visibleCardFields})

  if (error) return null

  const {styleCssVariables} = catalog
  return (
    <ErrorBoundary>
      <div ref={ref} className={classNames(css.wrapper, wrapperClassNames)} data-is-popover={props?.isPopover} style={styleCssVariables}>
        {loading && !value ? (shapeStore ? <div className={css.loading} />: null) :
        <BuilderCard
          className={classNames(css.card, loading && css.loading, className)}
          data={data}
          showImage
          workspace={workspace?.config}
          {..._.omit(props, ['esIndex'])}
          visibleCardFields={visibleCardFields}
        />}
      </div>
    </ErrorBoundary>
  )
}, {forwardRef: true}))


// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardLoader.tsx